import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { bulkSaveTargets } from '../../../redux/features/targetManagementSlice';

// CSV download -> edit -> re-upload, same pattern as
// views/finance/FeeForecastLatest/BulkUpload.jsx - plain CSV, no library, since that's already
// the established convention here for "bulk edit N rows" tools in this app.
const downloadCSV = (rows) => {
  const headers = ['userID', 'name', 'target', 'slot'];
  const csvRows = [
    headers.join(','),
    ...rows.map((r) => [r.userID, `"${r.name ?? ''}"`, r.target ?? '', r.slot ?? ''].join(',')),
  ];
  // Leading BOM so Excel on Windows reads this as UTF-8 instead of falling back to the
  // system codepage and garbling the ₹ symbol / any non-ASCII name.
  const blob = new Blob(['﻿' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'cro_targets_template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Quote-aware split - a plain `line.split(',')` breaks the moment any name contains a comma
// (e.g. "SURNAME, FIRSTNAME"), silently shifting every column after it on that row.
const splitCsvLine = (line) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells;
};

const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '').map(splitCsvLine);
  const headers = lines[0].map((h) => h.replace(/"/g, '').trim());
  return lines
    .slice(1)
    .map((row) => {
      const obj = {};
      headers.forEach((h, i) => { obj[h] = row[i]?.trim() ?? ''; });
      return obj;
    })
    .filter((r) => r.userID);
};

export default function BulkTargetUpload({ rows, month, disabled, onSaved }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { saving } = useSelector((state) => state.targetManagement);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  const handleUpload = () => {
    if (!file) return;
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      const existingByUserId = new Map(rows.map((r) => [String(r.userID), r]));

      // Target and Slot are both required (NOT NULL) on the backend - a row with a real
      // Target but no Slot anywhere (CSV blank AND no existing value, e.g. a brand-new
      // person) can't be saved as-is. Rather than dropping it, default the missing side to 0
      // and say so - only a row with NOTHING at all (neither field, nowhere) is skipped.
      const skipped = [];
      const defaulted = [];
      const entries = [];
      parsed.forEach((r) => {
        const existing = existingByUserId.get(String(r.userID));
        const hasTargetInput = r.target !== '';
        const hasSlotInput = r.slot !== '';
        const hasExistingTarget = existing?.target !== null && existing?.target !== undefined;
        const hasExistingSlot = existing?.slot !== null && existing?.slot !== undefined;

        if (!hasTargetInput && !hasSlotInput && !hasExistingTarget && !hasExistingSlot) {
          skipped.push(r.name || r.userID);
          return;
        }

        let target = hasTargetInput ? Number(r.target) : existing?.target;
        let slot = hasSlotInput ? Number(r.slot) : existing?.slot;
        const defaultedFields = [];
        if (target === null || target === undefined || Number.isNaN(target)) {
          target = 0;
          defaultedFields.push('Target');
        }
        if (slot === null || slot === undefined || Number.isNaN(slot)) {
          slot = 0;
          defaultedFields.push('Slot');
        }
        if (defaultedFields.length) {
          defaulted.push(`${r.name || r.userID} (${defaultedFields.join(' & ')} → 0)`);
        }
        entries.push({ assigned_to: Number(r.userID), target, slot });
      });

      if (!entries.length) {
        setError('No rows found with anything to save - every row was completely untouched.');
        return;
      }

      dispatch(bulkSaveTargets({ entries, month })).then((res) => {
        if (res.payload?.status === 'error') {
          setError(res.payload.message);
          return;
        }
        const notes = [];
        if (skipped.length) notes.push(`skipped ${skipped.length} untouched: ${skipped.join(', ')}`);
        if (defaulted.length) notes.push(`defaulted to 0: ${defaulted.join(', ')}`);
        enqueueSnackbar(
          notes.length ? `${entries.length} target(s) saved - ${notes.join('; ')}` : `${entries.length} target(s) saved`,
          { variant: notes.length ? 'warning' : 'success' },
        );
        setOpen(false);
        setFile(null);
        onSaved?.();
      });
    };
    reader.readAsText(file);
  };

  return (
    <GlobalDialog
      title="Bulk Set Targets"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="sm"
      closeButton
      closeCondition={() => setOpen(false)}
      popupButton={
        <Button size="small" variant="contained" disabled={disabled} onClick={() => setOpen(true)}>
          Bulk Upload
        </Button>
      }
      content={
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            Download the CSV and edit Target and/or Slot for whoever you're updating - you don't need to touch
            every column, a blank cell just keeps that person's current value. Rows you don't touch at all are
            ignored.
          </Typography>

          <Button
            startIcon={<Icon icon="line-md:download-loop" width={22} height={22} />}
            variant="outlined"
            onClick={() => downloadCSV(rows)}
            disabled={!rows.length}
          >
            Download CSV
          </Button>

          <Button startIcon={<Icon icon="material-symbols:upload" width={22} height={22} />} variant="outlined" component="label">
            Select CSV File
            <input
              hidden
              type="file"
              accept=".csv"
              onChange={(e) => { setFile(e.target.files[0]); setError(null); }}
            />
          </Button>

          {file && (
            <Typography variant="body2">
              Selected file: <b>{file.name}</b>
            </Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={saving || !file}
              startIcon={saving ? <CircularProgress size={18} /> : null}
            >
              {saving ? 'Uploading...' : 'Upload'}
            </Button>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      }
    />
  );
}
